// @flow
import * as React from 'react';
import debounce from 'lodash/debounce';
import {
    TextInput, View, useWindowDimensions, Image, Pressable, ActivityIndicator,
} from 'react-native';
import { FormattedMessage, useIntl } from 'react-intl';
import range from 'lodash/range';
import memoizeOne from 'memoize-one';
import { commonStyles } from 'src/styles/common';
import { PRIMARY_COLOR } from 'coooltv-js-shared/src/styles/common';
import Text from 'src/components/CustomText/CustomText';
import styles from 'src/navigation/Profile/ProfileStyles';
import { PROFILE_IMAGES } from 'src/navigation/constants';
import ArrowIcon from 'src/Icons/ArrowIcon';
import { pageStyles } from 'src/navigation/defaultPageStyles';

type Props = {
    errors: Object,
    nameOrPinCodeViewRef: React.Ref,
    onChange: Function,
    values: Object,
    handleSubmit: Function,
    isDefault: boolean,
    editing: boolean,
};

const ProfileInputsForm = (props: Props) => {
    const {
        errors, nameOrPinCodeViewRef, onChange, values: initialValues, handleSubmit, isDefault, editing,
    } = props;
    const { width } = useWindowDimensions();
    const [focusedProfilePictureIndex, setFocusedProfilePicture] = React.useState(editing ? null : 2);
    const [nonNumericCodeInput, setNonNumericCodeInput] = React.useState(false);
    const [values, setValues] = React.useState({
        name: '',
        pin_code: null,
        avatar: editing ? null : 'profile',
    });

    React.useEffect(() => {
        if (initialValues.avatar) {
            const index = PROFILE_IMAGES.findIndex((el) => el.name === initialValues.avatar.toLowerCase());
            setFocusedProfilePicture(index);
        }
    }, [initialValues.avatar]);

    const takeSlice = memoizeOne((allProfilePictures, index: number): ({
        profileImageIndex: number,
        profileImage: Object,
    }[]) => {
        const numToRender = width > 900 ? 5 : 3;
        const half = Math.ceil(numToRender / 2);
        const indices = range(index - half + 1, index).concat(range(index, index + half));
        const result = [];

        indices.forEach((_index) => {
            let realIndex = _index;
            if (realIndex < 0) {
                realIndex = allProfilePictures.length + _index;
            } else if (realIndex > allProfilePictures.length - 1) {
                realIndex -= allProfilePictures.length;
            }
            result.push({
                profileImageIndex: realIndex,
                profileImage: allProfilePictures[realIndex],
            });
        });
        return result;
    });

    const profileImagesSlice = (focusedProfilePictureIndex || focusedProfilePictureIndex === 0)
        ? takeSlice(PROFILE_IMAGES, focusedProfilePictureIndex || 0)
        : null;
    const intl = useIntl();

    React.useEffect(() => {
        setValues(initialValues);
    }, [initialValues]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleChange = React.useCallback(debounce((_values) => {
        onChange(_values);
    }, 300), [onChange]);

    React.useEffect(() => {
        handleChange(values);
    }, [values, handleChange]);

    return (
        <View style={width > 600 ? pageStyles.wrapper : pageStyles.wrapper_phone}>

            <View
                ref={nameOrPinCodeViewRef}
                style={width > 900 ? pageStyles.inputs_wrapper : pageStyles.inputs_wrapper_phone}
            >
                {!isDefault && (
                    <>
                        <TextInput
                            onSubmitEditing={handleSubmit}
                            onChangeText={(ch) => setValues({ ...values, name: ch })}
                            style={width > 600 ? commonStyles.input : commonStyles.input_phone}
                            value={values.name ? values.name : ''}
                            placeholder={intl.formatMessage({ id: 'username', defaultMessage: 'Username' })}
                        />
                        {errors?.name ? <Text style={pageStyles.text_input_error}>{errors.name}</Text> : null}
                    </>
                )}

                <TextInput
                    onSubmitEditing={handleSubmit}
                    onChangeText={(ch) => {
                        if (ch === '') {
                            setValues({ ...values, pin_code: null });
                            return;
                        }
                        const notNumericRegex = /[^0-9]/g;
                        if (notNumericRegex.test(ch)) {
                            setNonNumericCodeInput(true);
                        } else {
                            setNonNumericCodeInput(false);
                        }
                        setValues({ ...values, pin_code: ch.replace(notNumericRegex, '') });
                    }}
                    value={values.pin_code ? values.pin_code : ''}
                    style={width > 600 ? commonStyles.input : commonStyles.input_phone}
                    keyboardType="numeric"
                    placeholder={intl.formatMessage({ id: 'security_code', defaultMessage: 'Security Code' })}
                />
                {nonNumericCodeInput && (
                    <Text style={pageStyles.text_input_error}>
                        <FormattedMessage id="enter-valid-number" defaultMessage="Enter a valid number" />
                    </Text>
                )}
                <Text style={pageStyles.text_input_error}>{errors?.pin_code && errors.pin_code}</Text>
            </View>
            <Text style={width > 600 ? styles.avatar_choice_label : styles.avatar_choice_label_phone}>
                {intl.formatMessage({ id: 'avatar_choice', defaultMessage: 'Avatar Choice' })}
            </Text>
            <View style={styles.carousel_wrapper}>
                <Pressable onPress={() => {
                    let indexToSet = focusedProfilePictureIndex - 1;
                    if (indexToSet < 0) {
                        indexToSet = PROFILE_IMAGES.length - 1;
                    }
                    setFocusedProfilePicture(indexToSet);
                    setValues({ ...values, avatar: PROFILE_IMAGES[indexToSet].name });
                }}
                >
                    <ArrowIcon />
                </Pressable>
                {(focusedProfilePictureIndex || focusedProfilePictureIndex === 0) && profileImagesSlice ? (
                    <View style={styles.choice_images_wrapper}>
                        {profileImagesSlice.map((el) => {
                            const { profileImage } = el;
                            return (
                                <Pressable
                                    key={profileImage.path}
                                    onPress={() => {
                                        const index = PROFILE_IMAGES.indexOf(profileImage);
                                        setFocusedProfilePicture(index);
                                        setValues({ ...values, avatar: PROFILE_IMAGES[index].name });
                                    }}
                                >
                                    <View style={width > 600 ? styles.choice_image_wrapper
                                        : styles.choice_image_wrapper_phone}
                                    >
                                        <Image
                                            style={[width > 600 ? styles.image_style
                                                : styles.choice_image_style_phone, profileImage.name
                                            === PROFILE_IMAGES[focusedProfilePictureIndex].name
                                            && (width > 600 ? styles.selected_profile_image
                                                : styles.selected_profile_image_phone)]}
                                            source={profileImage.path}
                                        />
                                    </View>
                                </Pressable>
                            );
                        })}
                    </View>
                ) : (
                    <ActivityIndicator size="large" color={PRIMARY_COLOR} />
                )}
                <Pressable
                    style={styles.right_arrow_style}
                    onPress={() => {
                        let indexToSet = focusedProfilePictureIndex + 1;
                        if (indexToSet > PROFILE_IMAGES.length - 1) {
                            indexToSet = 0;
                        }
                        setFocusedProfilePicture(indexToSet);
                        setValues({ ...values, avatar: PROFILE_IMAGES[indexToSet].name });
                    }}
                >
                    <ArrowIcon />
                </Pressable>
            </View>
        </View>
    );
};

export default ProfileInputsForm;
